import { useEffect, useState } from 'react';

import { randomUUID as uuidv4 } from 'expo-crypto';
import { isEmpty, pick } from 'lodash-es';

import { IItem, ILegacyCartEntry } from '@rbi-ctg/menu';
import { useCart } from 'hooks/use-cart';
import { IAddOnSection, IAddOnSectionOption } from 'hooks/use-feature-menu-add-on';
import useFoodware from 'hooks/use-foodware';
import { useMenuContext } from 'state/menu';
import {
  convertModActionsToSelections,
  getSelectionsFromMenuData,
  transformMenuObjectToCartItem,
} from 'utils/wizard';

export type AddOnSelections = Record<
  string,
  { quantity: number; data: IItem; sectionId: string; cartId?: string }
>;
export type { IAddOnSection, IAddOnSectionOption };

interface IUseCartAddOnsOptions {
  initialSelections?: AddOnSelections;
  addOnData?: IAddOnSection[];
}

const useCartAddOns = (options?: IUseCartAddOnsOptions) => {
  const { initialSelections = {}, addOnData = [] } = options ?? {};
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [addOnSelections, setAddOnSelections] = useState<AddOnSelections>(initialSelections);
  const [previousSelections, setPreviousSelections] = useState<AddOnSelections>({});
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [totalQuantity, setTotalQuantity] = useState<number>(0);
  const { isFoodware } = useFoodware();

  const { pricingFunction } = useMenuContext();

  const { addItemToCart, cartEntries, removeFromCart } = useCart();

  const addExtraItemToCart = (mainEntry: ILegacyCartEntry, index: number, totalItems: number) => {
    // Only show message for the last item, to avoid showing multiple messages
    const showMessage = index === totalItems - 1;
    const newCartId = uuidv4();
    addItemToCart(
      { ...mainEntry, isExtra: true, cartId: newCartId, quantity: 1 },
      undefined,
      showMessage
    );
  };

  // NOTE: Currently this only handles adding IItem menu item type as that is all addOns currently support in Sanity
  // TODO: Review this logic in order to make sure that all of this is right after cart v2 is enabled.
  // See https://rbictg.atlassian.net/browse/BKPE-22225
  const handleAddToCart = () => {
    // just navigate to the cart nothing was selected. Treat this as if they exited the modal
    if (totalQuantity === 0 && isEmpty(previousSelections)) {
      return;
    }

    const extraItems = cartEntries.reduce(
      (acc: Record<string, ILegacyCartEntry>, entry: ILegacyCartEntry) => {
        if (entry.isExtra) {
          acc[entry._id] = entry;
        }
        return acc;
      },
      {}
    );

    for (const addOnKey in previousSelections) {
      // Item is not in current add on selections so remove it
      const cartId = previousSelections[addOnKey].cartId;
      if (!addOnSelections[addOnKey] && !!cartId) {
        removeFromCart({ cartId });
      }
    }

    for (const addOnKey in addOnSelections) {
      const { quantity, data, cartId: existingCartId } = addOnSelections[addOnKey];
      // Make sure to always get the default modifiers for the item
      const { modifierSelections: modSelectionActions } = getSelectionsFromMenuData({ data });
      // Convert the returned action into wizard state
      const modifierSelections = modSelectionActions
        ? convertModActionsToSelections(modSelectionActions)
        : [];

      // Make sure to always include modifiers that are injected by default by passing it through the same function wizard uses
      const { mainEntry } = transformMenuObjectToCartItem(data, {
        quantity,
        modifierSelections,
        existingCartId,
      });

      const isExtraItemInCart = !!extraItems[data._id];

      if (mainEntry && !isExtraItemInCart) {
        // If cart v2 is enabled, quantity will match the amount of that item in the cart.
        Array.from({ length: quantity }).forEach((_, index) =>
          addExtraItemToCart(mainEntry, index, quantity)
        );
      } else if (
        mainEntry &&
        isExtraItemInCart &&
        quantity > 0 &&
        quantity !== previousSelections[addOnKey].quantity
      ) {
        const existingCartId = previousSelections[addOnKey].cartId || '';
        const existingSameCartEntries = cartEntries.filter(entry => entry._id === mainEntry._id);
        const totalQuantityOfSameAddOnKey = existingSameCartEntries.length;

        // Create new items if the quantity is greater than the existing items
        if (quantity > totalQuantityOfSameAddOnKey) {
          const itemsToAdd = quantity - totalQuantityOfSameAddOnKey;
          Array.from({ length: itemsToAdd }).forEach((_, index) =>
            addExtraItemToCart(mainEntry, index, itemsToAdd)
          );
        }

        // Since when cart v2 is enabled, an item's quantity will match the amount of that item in the cart,
        // we need to remove excess cart items from cart if the new quantity is less than the existing items
        if (quantity < totalQuantityOfSameAddOnKey) {
          const quantityToRemove = totalQuantityOfSameAddOnKey - quantity;

          existingSameCartEntries
            .filter(entry => entry.cartId !== existingCartId)
            .slice(0, quantityToRemove)
            .forEach(entry => removeFromCart({ cartId: entry.cartId }));
        }
      }
    }
  };

  // If the new quantity is zero remove that options itemId from the selections completely
  // Otherwise update that key to the new quantity/data
  const updateQuantityForItem = (
    sectionOption: IAddOnSectionOption,
    quantity: number,
    section: IAddOnSection
  ) => {
    const optionId = sectionOption._key;
    const data = sectionOption.option;
    const sectionId = section._id;

    if (quantity === 0) {
      const totalQuantityOfSameAddOnKey = cartEntries.filter(entry => entry._id === data._id);
      totalQuantityOfSameAddOnKey.map(entry => {
        return removeFromCart({ cartId: entry.cartId });
      });
    }

    setAddOnSelections(currentSelections =>
      quantity === 0
        ? pick(
            currentSelections,
            Object.keys(currentSelections).filter(key => key !== optionId)
          )
        : {
            ...currentSelections,
            [optionId]: {
              quantity,
              data,
              sectionId,
              cartId: currentSelections[optionId] && currentSelections[optionId].cartId,
            },
          }
    );
  };

  // Recalculate the total anytime the selections change.
  useEffect(() => {
    const { calculatedPrice: newPrice, calculatedQuantity: newQuantity } = Object.keys(
      addOnSelections
    ).reduce(
      ({ calculatedPrice, calculatedQuantity }, selectionKey) => {
        const { quantity, data } = addOnSelections[selectionKey];
        return {
          calculatedPrice: calculatedPrice + pricingFunction({ item: data, quantity }),
          calculatedQuantity: calculatedQuantity + quantity,
        };
      },
      { calculatedPrice: 0, calculatedQuantity: 0 }
    );
    setTotalPrice(newPrice);
    setTotalQuantity(newQuantity);
  }, [addOnSelections, pricingFunction]);

  // Preload selections when we mount.
  useEffect(() => {
    // if no addOnData or cartEntries don't do anything
    if (!addOnData.length || !cartEntries.length) {
      return;
    }

    // get an object of all extra entries with keys being the items id
    const cartEntrySelections = cartEntries.reduce(
      (selections: Record<string, ILegacyCartEntry>, entry: ILegacyCartEntry) => {
        if (entry.isExtra && !isFoodware(entry)) {
          return { ...selections, [entry._id]: entry };
        }
        return selections;
      },
      {}
    );

    if (!Object.keys(cartEntrySelections).length) {
      return;
    }

    setIsEdit(true);

    const preloadedSelections = addOnData.reduce((selections: AddOnSelections, section) => {
      let sectionSelections = {};
      section.options.forEach(({ _key: selectionKey, option: item }) => {
        if (cartEntrySelections[item._id]) {
          const quantity = cartEntries.filter(entry => entry._id === item._id).length;

          sectionSelections = {
            ...sectionSelections,
            [selectionKey]: {
              quantity,
              data: item,
              sectionId: section._id,
              cartId: cartEntrySelections[item._id].cartId,
            },
          };
        }
      });
      return { ...selections, ...sectionSelections };
    }, {});

    setPreviousSelections(preloadedSelections); // hold onto the selections we load with before the guest makes changes
    setAddOnSelections(preloadedSelections);
  }, [addOnData, cartEntries]);

  return {
    handleAddToCart,
    isEdit,
    updateQuantityForItem,
    addOnSelections,
    totalPrice,
    totalQuantity,
  };
};

export default useCartAddOns;
